import type { CSSProperties, ChangeEvent } from 'react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { FoodItem, MealType } from '../types'

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  meal: MealType
  date: string
  initialName?: string
  initialBarcode?: string
  onClose: () => void
  onCreated: (food: FoodItem) => void
}

type Mode = 'nip' | 'quick'

// ─── Energy helpers ───────────────────────────────────────────────────────────

const KJ_PER_KCAL = 4.184

// Australian NIP energy factors (kJ per gram) — FSANZ standard
const KJ_PROTEIN = 17
const KJ_CARBS   = 17
const KJ_FAT     = 37
const KJ_FIBRE   = 8

function kcalToKj(kcal: string): string {
  const n = parseFloat(kcal)
  return isNaN(n) ? '' : String(Math.round(n * KJ_PER_KCAL))
}

function kjToKcal(kj: string): string {
  const n = parseFloat(kj)
  return isNaN(n) ? '' : String(Math.round(n / KJ_PER_KCAL))
}

// Calculate kJ from macros using AU NIP factors
// Returns null if not enough data (need protein + carbs + fat at minimum)
function calcMacroKj(
  protein: string,
  carbs: string,
  fat: string,
  fibre: string,
): number | null {
  const p = parseFloat(protein)
  const c = parseFloat(carbs)
  const f = parseFloat(fat)
  if (isNaN(p) || isNaN(c) || isNaN(f)) return null
  const fb = parseFloat(fibre) || 0
  return Math.round(p * KJ_PROTEIN + c * KJ_CARBS + f * KJ_FAT + fb * KJ_FIBRE)
}

// ─── Component ────────────────────────────────────────────────────────────────

export function FoodCreateModal({ meal, date, initialName = '', initialBarcode, onClose, onCreated }: Props) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [mode, setMode] = useState<Mode>('nip')

  // Shared
  const [name, setName] = useState(initialName)

  // NIP fields (per 100g / 100mL)
  const [energyKcal, setEnergyKcal] = useState('')
  const [energyKj, setEnergyKj]     = useState('')
  const [protein, setProtein]       = useState('')
  const [fat, setFat]               = useState('')
  const [satFat, setSatFat]         = useState('')
  const [carbs, setCarbs]           = useState('')
  const [sugars, setSugars]         = useState('')
  const [fibre, setFibre]           = useState('')
  const [sodium, setSodium]         = useState('')
  const [servingSize, setServingSize]         = useState('100')
  const [servingsPerPack, setServingsPerPack] = useState('')

  // Quick mode
  const [quickCal, setQuickCal]   = useState('')
  const [quickName, setQuickName] = useState(initialName)

  // OCR state
  const [ocrLoading, setOcrLoading] = useState(false)
  const [ocrError, setOcrError]     = useState<string | null>(null)

  // ─── Validation ───────────────────────────────────────────────────────────

  // Sub-nutrient: sat fat must be ≤ total fat
  const fatNum    = parseFloat(fat)
  const satFatNum = parseFloat(satFat)
  const satFatError =
    !isNaN(satFatNum) && !isNaN(fatNum) && satFatNum > fatNum
      ? 'Must be ≤ total fat'
      : null

  // Sub-nutrient: sugars must be ≤ carbs
  const carbsNum  = parseFloat(carbs)
  const sugarsNum = parseFloat(sugars)
  const sugarsError =
    !isNaN(sugarsNum) && !isNaN(carbsNum) && sugarsNum > carbsNum
      ? 'Must be ≤ carbohydrates'
      : null

  // Macro-derived energy
  const calcKj   = calcMacroKj(protein, carbs, fat, fibre)
  const calcKcal = calcKj != null ? Math.round(calcKj / KJ_PER_KCAL) : null

  // Energy divergence check (only when user has entered energy AND macros give a result)
  const enteredKj   = parseFloat(energyKj)
  const energyEntered = energyKcal !== '' && !isNaN(enteredKj)
  const divergePct =
    calcKj != null && energyEntered && calcKj > 0
      ? Math.round((Math.abs(calcKj - enteredKj) / calcKj) * 100)
      : null
  const withinTolerance = divergePct != null && divergePct <= 10

  // Apply the calculated values to the energy fields
  const applyCalcEnergy = () => {
    if (calcKj == null || calcKcal == null) return
    setEnergyKj(String(calcKj))
    setEnergyKcal(String(calcKcal))
  }

  // ─── Calculated pack totals ───────────────────────────────────────────────

  const kcalPer100    = parseFloat(energyKcal)
  const servingSizeG  = parseFloat(servingSize) || 100
  const kcalPerServing =
    !isNaN(kcalPer100) ? Math.round((kcalPer100 * servingSizeG) / 100) : null
  const packServings = parseFloat(servingsPerPack)
  const kcalPerPack  =
    !isNaN(packServings) && kcalPerServing != null
      ? Math.round(kcalPerServing * packServings)
      : null

  // ─── Mutations ────────────────────────────────────────────────────────────

  const createFood = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.foods.create(body) as Promise<{ id: string }>,
    onSuccess: async (result) => {
      const food = (await api.foods.byId(result.id)) as FoodItem
      queryClient.invalidateQueries({ queryKey: ['foods'] })
      onCreated(food)
    },
  })

  const quickLog = useMutation({
    mutationFn: async () => {
      const cal      = parseFloat(quickCal)
      const foodName = quickName.trim() || 'Quick entry'
      const result   = (await api.foods.create({
        name: foodName,
        calories: cal,
        serving_size_g: 100,
        serving_unit: 'g',
      })) as { id: string }
      await api.logs.food.add({
        log_date: date,
        meal_type: meal,
        food_item_id: result.id,
        servings: 1,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['summary', 'day', date] })
      queryClient.invalidateQueries({ queryKey: ['foods', 'recent'] })
      navigate('/day')
    },
  })

  // ─── OCR handler ─────────────────────────────────────────────────────────

  const handleOcrCapture = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setOcrError(null)
    setOcrLoading(true)

    try {
      // Resize to max 1200px wide to keep payload small
      const bitmap = await createImageBitmap(file)
      const maxW = 1200
      const scale = bitmap.width > maxW ? maxW / bitmap.width : 1
      const canvas = document.createElement('canvas')
      canvas.width  = Math.round(bitmap.width  * scale)
      canvas.height = Math.round(bitmap.height * scale)
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      const base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1]

      const resp = await fetch('/api/foods/ocr-nip', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image: base64, mediaType: 'image/jpeg' }),
      })

      if (!resp.ok) {
        const err = await resp.json() as { error?: string }
        throw new Error(err.error ?? 'OCR failed')
      }

      const nip = await resp.json() as {
        food_name?: string | null
        brand?: string | null
        serving_size_g?: number | null
        servings_per_pack?: number | null
        energy_kj?: number | null
        energy_kcal?: number | null
        protein_g?: number | null
        fat_g?: number | null
        saturated_fat_g?: number | null
        carbs_g?: number | null
        sugars_g?: number | null
        dietary_fibre_g?: number | null
        sodium_mg?: number | null
      }

      // Populate fields — only overwrite if a value was found
      if (nip.food_name)          setName(nip.food_name)
      if (nip.energy_kj != null)  { setEnergyKj(String(nip.energy_kj)); setEnergyKcal(kjToKcal(String(nip.energy_kj))) }
      if (nip.energy_kcal != null && !nip.energy_kj) { setEnergyKcal(String(nip.energy_kcal)); setEnergyKj(kcalToKj(String(nip.energy_kcal))) }
      if (nip.protein_g != null)       setProtein(String(nip.protein_g))
      if (nip.fat_g != null)           setFat(String(nip.fat_g))
      if (nip.saturated_fat_g != null) setSatFat(String(nip.saturated_fat_g))
      if (nip.carbs_g != null)         setCarbs(String(nip.carbs_g))
      if (nip.sugars_g != null)        setSugars(String(nip.sugars_g))
      if (nip.dietary_fibre_g != null) setFibre(String(nip.dietary_fibre_g))
      if (nip.sodium_mg != null)       setSodium(String(nip.sodium_mg))
      if (nip.serving_size_g != null)  setServingSize(String(nip.serving_size_g))
      if (nip.servings_per_pack != null) setServingsPerPack(String(nip.servings_per_pack))
    } catch (err: any) {
      setOcrError(err?.message ?? 'Could not read label')
    } finally {
      setOcrLoading(false)
      // Reset file input so same photo can be re-submitted
      e.target.value = ''
    }
  }

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleKcalChange = (v: string) => {
    setEnergyKcal(v)
    setEnergyKj(kcalToKj(v))
  }

  const handleKjChange = (v: string) => {
    setEnergyKj(v)
    setEnergyKcal(kjToKcal(v))
  }

  const handleNipSave = () => {
    const cal = parseFloat(energyKcal)
    if (!name.trim() || isNaN(cal) || satFatError || sugarsError) return
    createFood.mutate({
      name:            name.trim(),
      barcode:         initialBarcode ?? null,
      calories:        cal,
      protein_g:       protein ? parseFloat(protein) : null,
      fat_g:           fat     ? parseFloat(fat)     : null,
      saturated_fat_g: satFat  ? parseFloat(satFat)  : null,
      carbs_g:         carbs   ? parseFloat(carbs)   : null,
      sugar_g:         sugars  ? parseFloat(sugars)  : null,
      fibre_g:         fibre   ? parseFloat(fibre)   : null,
      sodium_mg:       sodium  ? parseFloat(sodium)  : null,
      serving_size_g:  servingSizeG,
      serving_unit:    'g',
      source:          initialBarcode ? 'user' : 'manual',
    })
  }

  const nipValid =
    name.trim().length > 0 &&
    energyKcal !== '' &&
    !isNaN(parseFloat(energyKcal)) &&
    !satFatError &&
    !sugarsError

  const quickValid = parseFloat(quickCal) > 0

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#09090b',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem 1rem',
          paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
          borderBottom: '1px solid #27272a',
          background: '#09090b',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button onClick={onClose} style={backBtn} aria-label="Back">←</button>
        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Create food</span>
      </div>

      {/* Mode tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #27272a' }}>
        {(['nip', 'quick'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${mode === m ? '#22d3ee' : 'transparent'}`,
              color: mode === m ? '#22d3ee' : '#71717a',
              fontSize: '0.78rem',
              fontWeight: mode === m ? 600 : 400,
              padding: '0.6rem 0',
              cursor: 'pointer',
            }}
          >
            {m === 'nip' ? 'Full NIP' : 'Quick calories'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, padding: '1rem 1rem 2rem' }}>
        {mode === 'nip' ? (
          <>
            {/* Scan NIP label button */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                width: '100%',
                padding: '0.65rem',
                marginBottom: '1rem',
                background: ocrLoading ? '#3f3f46' : '#18181b',
                border: '1.5px dashed #52525b',
                borderRadius: 10,
                color: ocrLoading ? '#a1a1aa' : '#e4e4e7',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: ocrLoading ? 'default' : 'pointer',
                pointerEvents: ocrLoading ? 'none' : 'auto',
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>📷</span>
              {ocrLoading ? 'Reading label…' : 'Scan NIP label'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleOcrCapture}
                style={{ display: 'none' }}
              />
            </label>

            {ocrError && (
              <p style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: '0.75rem', textAlign: 'center' }}>
                {ocrError}
              </p>
            )}

            {/* Name */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>Food name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Chicken breast, grilled"
                style={textInput}
                autoFocus
              />
            </div>

            {/* Section: Per 100g */}
            <div style={sectionHead}>Per 100g / 100mL</div>

            {/* Energy — dual kcal/kJ entry */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>Energy *</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="number"
                    value={energyKcal}
                    onChange={(e) => handleKcalChange(e.target.value)}
                    placeholder="0"
                    min="0"
                    style={numInput}
                  />
                  <div style={unitTag}>kcal</div>
                </div>
                <span style={{ color: '#3f3f46', fontSize: '0.85rem', paddingTop: '0.5rem' }}>=</span>
                <div style={{ flex: 1 }}>
                  <input
                    type="number"
                    value={energyKj}
                    onChange={(e) => handleKjChange(e.target.value)}
                    placeholder="0"
                    min="0"
                    style={numInput}
                  />
                  <div style={unitTag}>kJ</div>
                </div>
              </div>
            </div>

            {/* ── Energy validation panel ── */}
            {calcKcal != null && (
              <div
                style={{
                  borderRadius: 8,
                  padding: '0.6rem 0.75rem',
                  marginBottom: '0.75rem',
                  fontSize: '0.78rem',
                  ...(
                    !energyEntered
                      ? { background: '#0f1f2e', border: '1px solid #1e3a4f' }
                      : withinTolerance
                      ? { background: '#0a1f0a', border: '1px solid #166534' }
                      : { background: '#1c1109', border: '1px solid #78350f' }
                  ),
                }}
              >
                {/* Top row */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.4rem',
                  }}
                >
                  <span
                    style={{
                      color: !energyEntered
                        ? '#38bdf8'
                        : withinTolerance
                        ? '#4ade80'
                        : '#fcd34d',
                    }}
                  >
                    {!energyEntered ? '→' : withinTolerance ? '✓' : '⚠'}{' '}
                    Macro calc: <strong>{calcKcal} kcal / {calcKj} kJ</strong>
                  </span>

                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    {!energyEntered ? (
                      // Auto-fill prompt when energy is blank
                      <button
                        onClick={applyCalcEnergy}
                        style={calcBtn}
                      >
                        Use this
                      </button>
                    ) : !withinTolerance && divergePct != null ? (
                      // Mismatch — show divergence + offer to fix
                      <>
                        <span style={{ fontSize: '0.7rem', color: '#92400e' }}>
                          {divergePct}% off
                        </span>
                        <button onClick={applyCalcEnergy} style={calcBtn}>
                          Use calculated
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>

                {/* Detail row */}
                <div style={{ marginTop: '0.3rem', fontSize: '0.7rem', color: '#52525b' }}>
                  {!energyEntered
                    ? 'Energy field is empty — tap "Use this" to fill from your macros.'
                    : withinTolerance
                    ? `Within ${divergePct ?? 0}% of macro calculation — looks good.`
                    : `Entered energy differs from macros by ${divergePct}%. Check for a kJ/kcal mix-up or typo.`}
                </div>
              </div>
            )}

            {/* Macro grid */}
            <MacroGrid
              fields={[
                { label: 'Protein',        val: protein, setter: setProtein, unit: 'g' },
                { label: 'Fat (total)',     val: fat,     setter: setFat,     unit: 'g' },
                { label: 'Saturated fat',  val: satFat,  setter: setSatFat,  unit: 'g', error: satFatError },
                { label: 'Carbohydrates',  val: carbs,   setter: setCarbs,   unit: 'g' },
                { label: 'Sugars',         val: sugars,  setter: setSugars,  unit: 'g', error: sugarsError },
                { label: 'Dietary fibre',  val: fibre,   setter: setFibre,   unit: 'g' },
                { label: 'Sodium',         val: sodium,  setter: setSodium,  unit: 'mg' },
              ]}
            />

            {/* Section: Serving info */}
            <div style={sectionHead}>Serving info</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
                marginBottom: '0.75rem',
              }}
            >
              <div>
                <label style={fieldLabel}>Serving size</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={servingSize}
                    onChange={(e) => setServingSize(e.target.value)}
                    min="1"
                    style={{ ...numInput, paddingRight: '2rem' }}
                  />
                  <UnitBadge>g</UnitBadge>
                </div>
              </div>
              <div>
                <label style={fieldLabel}>Servings per pack</label>
                <input
                  type="number"
                  value={servingsPerPack}
                  onChange={(e) => setServingsPerPack(e.target.value)}
                  placeholder="e.g. 4"
                  min="1"
                  style={numInput}
                />
              </div>
            </div>

            {/* Calculated serving / pack totals */}
            {(kcalPerServing != null || kcalPerPack != null) && (
              <div
                style={{
                  background: '#18181b',
                  border: '1px solid #27272a',
                  borderRadius: 8,
                  padding: '0.6rem 0.75rem',
                  marginBottom: '1.25rem',
                  fontSize: '0.78rem',
                  color: '#a1a1aa',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                }}
              >
                {kcalPerServing != null && (
                  <span>
                    Per serving ({servingSize}g):{' '}
                    <strong style={{ color: '#fafafa' }}>{kcalPerServing} kcal</strong>
                  </span>
                )}
                {kcalPerPack != null && (
                  <span>
                    Whole pack:{' '}
                    <strong style={{ color: '#fafafa' }}>{kcalPerPack} kcal</strong>
                  </span>
                )}
              </div>
            )}

            {createFood.isError && (
              <p style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                Failed to save. Try again.
              </p>
            )}

            <button
              onClick={handleNipSave}
              disabled={!nipValid || createFood.isPending}
              style={{
                ...saveBtn,
                opacity: !nipValid || createFood.isPending ? 0.5 : 1,
                cursor: !nipValid || createFood.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {createFood.isPending ? 'Saving…' : 'Save food'}
            </button>

            {!nipValid && (name || energyKcal) && !satFatError && !sugarsError && (
              <p style={{ fontSize: '0.72rem', color: '#52525b', marginTop: '0.5rem', textAlign: 'center' }}>
                Name and energy are required
              </p>
            )}
          </>
        ) : (
          // ── Quick calories mode ──
          <>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Food name (optional)</label>
              <input
                value={quickName}
                onChange={(e) => setQuickName(e.target.value)}
                placeholder="e.g. Chicken salad"
                style={textInput}
              />
            </div>

            <div style={{ ...fieldGroup, marginBottom: '0.5rem' }}>
              <label style={fieldLabel}>Calories *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  value={quickCal}
                  onChange={(e) => setQuickCal(e.target.value)}
                  placeholder="0"
                  min="1"
                  autoFocus={mode === 'quick'}
                  style={{
                    ...numInput,
                    fontSize: '2rem',
                    fontWeight: 700,
                    padding: '0.75rem 4rem 0.75rem 0.75rem',
                    color: '#fafafa',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '0.9rem',
                    color: '#71717a',
                    pointerEvents: 'none',
                  }}
                >
                  kcal
                </span>
              </div>
            </div>

            <p style={{ fontSize: '0.78rem', color: '#52525b', marginBottom: '1.5rem' }}>
              Adds a flat calorie entry directly to your {meal} log. No macros tracked.
            </p>

            {quickLog.isError && (
              <p style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                Failed to log. Try again.
              </p>
            )}

            <button
              onClick={() => quickLog.mutate()}
              disabled={!quickValid || quickLog.isPending}
              style={{
                ...saveBtn,
                opacity: !quickValid || quickLog.isPending ? 0.5 : 1,
                cursor: !quickValid || quickLog.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {quickLog.isPending ? 'Adding…' : `Add ${quickCal || '0'} kcal to ${meal}`}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── MacroGrid sub-component ──────────────────────────────────────────────────

interface MacroFieldDef {
  label: string
  val: string
  setter: (v: string) => void
  unit: string
  error?: string | null
}

function MacroGrid({ fields }: { fields: MacroFieldDef[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.5rem',
        marginBottom: '0.75rem',
      }}
    >
      {fields.map(({ label, val, setter, unit, error }) => (
        <div key={label}>
          <label
            style={{
              display: 'block',
              fontSize: '0.68rem',
              color: error ? '#f87171' : '#71717a',
              marginBottom: '0.3rem',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.05em',
            }}
          >
            {label}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              value={val}
              onChange={(e) => setter(e.target.value)}
              placeholder="0"
              min="0"
              style={{
                width: '100%',
                background: error ? '#1f0a0a' : '#18181b',
                border: `1px solid ${error ? '#7f1d1d' : '#27272a'}`,
                borderRadius: 8,
                color: '#fafafa',
                fontSize: '0.9rem',
                padding: '0.55rem 2.25rem 0.55rem 0.65rem',
                outline: 'none',
                boxSizing: 'border-box' as const,
              }}
            />
            <UnitBadge color={error ? '#7f1d1d' : undefined}>{unit}</UnitBadge>
          </div>
          {error && (
            <p style={{ fontSize: '0.65rem', color: '#f87171', marginTop: 3 }}>{error}</p>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── UnitBadge helper ─────────────────────────────────────────────────────────

function UnitBadge({ children, color }: { children: string; color?: string }) {
  return (
    <span
      style={{
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '0.68rem',
        color: color ?? '#52525b',
        pointerEvents: 'none',
      }}
    >
      {children}
    </span>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const backBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#fafafa',
  cursor: 'pointer',
  fontSize: '1.1rem',
  padding: 0,
  lineHeight: 1,
}

const fieldGroup: CSSProperties = {
  marginBottom: '0.75rem',
}

const fieldLabel: CSSProperties = {
  display: 'block',
  fontSize: '0.68rem',
  color: '#71717a',
  marginBottom: '0.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const sectionHead: CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 600,
  color: '#52525b',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  paddingBottom: '0.3rem',
  borderBottom: '1px solid #18181b',
  marginBottom: '0.6rem',
  marginTop: '0.25rem',
}

const textInput: CSSProperties = {
  width: '100%',
  background: '#18181b',
  border: '1px solid #27272a',
  borderRadius: 8,
  color: '#fafafa',
  fontSize: '0.9rem',
  padding: '0.55rem 0.65rem',
  outline: 'none',
  boxSizing: 'border-box',
}

const numInput: CSSProperties = {
  width: '100%',
  background: '#18181b',
  border: '1px solid #27272a',
  borderRadius: 8,
  color: '#fafafa',
  fontSize: '0.9rem',
  padding: '0.55rem 0.65rem',
  outline: 'none',
  boxSizing: 'border-box',
}

const unitTag: CSSProperties = {
  fontSize: '0.65rem',
  color: '#52525b',
  textAlign: 'center',
  marginTop: 3,
}

const saveBtn: CSSProperties = {
  width: '100%',
  background: '#22d3ee',
  border: 'none',
  borderRadius: 10,
  color: '#09090b',
  fontSize: '0.95rem',
  fontWeight: 700,
  padding: '0.8rem',
}

const calcBtn: CSSProperties = {
  background: '#27272a',
  border: '1px solid #3f3f46',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: '0.72rem',
  fontWeight: 600,
  padding: '0.25rem 0.5rem',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}
